// src/components/Calculator/MonthlyDonutChart.tsx

import { useState, useEffect } from 'react';
import { PieChart, Info } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import * as Popover from '@radix-ui/react-popover';

// Add simple tooltip component
interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
}

function Tooltip({ children, content }: TooltipProps) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button className="inline-flex items-center gap-1 hover:text-gray-900">
          {children}
          <Info className="w-3.5 h-3.5 text-gray-400" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 w-72 p-3 bg-white rounded-lg shadow-lg border border-gray-200 text-sm text-gray-600"
          sideOffset={1}>
          {content}
          <Popover.Arrow className="fill-white" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

interface ChartSegment {
  path: string;
  color: string;
  dashArray: number;
  dashOffset: number;
  label: string;
  percentage: number;
}

interface ChartDataItem {
  label: string;
  value: number;
  total: number;
  color: string;
  percentage: number;
}

interface DonutChartProps {
  data: ChartDataItem[];
  size?: number;
  strokeWidth?: number;
  activeIndex: number | null;
  onHover: (index: number | null) => void;
}

const DonutChart = ({ data, size = 160, strokeWidth = 30, activeIndex, onHover }: DonutChartProps) => {
  const [segments, setSegments] = useState<ChartSegment[]>([]);
  const [popupPositions, setPopupPositions] = useState<Array<{x: number, y: number}>>([]);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  
  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  useEffect(() => {
    // Calculate segments
    let startAngle = 0;
    const calculatedSegments = data.map(item => {
      const fraction = item.value / item.total;
      const angle = fraction * 2 * Math.PI;
      
      // Calculate SVG arc parameters
      const x1 = radius * Math.cos(startAngle) + radius + strokeWidth/2;
      const y1 = radius * Math.sin(startAngle) + radius + strokeWidth/2;
      const x2 = radius * Math.cos(startAngle + angle) + radius + strokeWidth/2;
      const y2 = radius * Math.sin(startAngle + angle) + radius + strokeWidth/2;
      
      // Determine if the arc should be drawn the long way around
      const largeArc = angle > Math.PI ? 1 : 0;
      
      // SVG path
      const path = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
      
      const segment: ChartSegment = {
        path,
        color: item.color,
        dashArray: circumference,
        dashOffset: circumference * (1 - fraction),
        label: item.label,
        percentage: Math.round(fraction * 100)
      };
      
      startAngle += angle;
      return segment;
    });
    
    setSegments(calculatedSegments);
  }, [data, radius, circumference, strokeWidth]);

  // Calculate popup positions for each segment
  useEffect(() => {
    let startAngle = 0;
    const positions: Array<{x: number, y: number}> = [];
    
    data.forEach(item => {
      const fraction = item.value / item.total;
      const angle = fraction * 2 * Math.PI;
      
      // Calculate midpoint angle (adjust for rotation)
      const midAngle = startAngle + (angle / 2) - Math.PI/2; // Adjust for the -rotate-90 CSS
      
      // Calculate position on the segment
      const segmentRadius = radius * 0.7; // Position at 70% of radius
      const x = size/2 + segmentRadius * Math.cos(midAngle);
      const y = size/2 + segmentRadius * Math.sin(midAngle);
      
      positions.push({ x, y });
      
      startAngle += angle;
    });
    
    setPopupPositions(positions);
  }, [data, radius, size]);
  
  // Handle interaction differently for mobile vs desktop
  const handleInteraction = (index: number | null, action: 'enter' | 'leave' | 'click') => {
    if (isMobile) {
      // On mobile, only respond to clicks
      if (action === 'click') {
        onHover(index === activeIndex ? null : index);
      }
    } else {
      // On desktop, respond to hover events
      if (action === 'enter') {
        onHover(index);
      } else if (action === 'leave') {
        onHover(null);
      } else if (action === 'click') {
        onHover(index === activeIndex ? null : index);
      }
    }
  };
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* SVG for donut segments (rotated) */}
      <svg 
        width={size} 
        height={size} 
        viewBox={`0 0 ${size + strokeWidth} ${size + strokeWidth}`}
        className="transform -rotate-90"
      >
        {segments.map((segment, index) => (
          <path
            key={index}
            d={segment.path}
            fill="none"
            stroke={segment.color}
            strokeWidth={activeIndex === index ? strokeWidth + 4 : strokeWidth}
            strokeDasharray={segment.dashArray}
            strokeDashoffset={0}
            className="transition-all duration-300 ease-in-out"
            style={{ 
              opacity: activeIndex !== null && activeIndex !== index ? 0.6 : 1,
              filter: activeIndex === index ? 'drop-shadow(0px 0px 3px rgba(0,0,0,0.3))' : 'none'
            }}
            onMouseEnter={() => handleInteraction(index, 'enter')}
            onMouseLeave={() => handleInteraction(index, 'leave')}
            onClick={() => handleInteraction(index, 'click')}
          />
        ))}
      </svg>

      {/* Popup Overlay */}
      {activeIndex !== null && popupPositions.length > activeIndex && (
        <div 
          className="absolute text-white rounded-md py-1 px-3 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none shadow-lg text-center z-10"
          style={{ 
            left: popupPositions[activeIndex].x, 
            top: popupPositions[activeIndex].y,
            backgroundColor: `${data[activeIndex].color}CC` // Add CC for ~80% opacity (hex)
          }}
        >
          <div className="font-bold text-xs whitespace-nowrap">{data[activeIndex].label}</div>
          <div className="font-bold text-sm">{formatCurrency(data[activeIndex].value)}</div>
          <div className="text-xs">{data[activeIndex].percentage}%</div>
        </div>
      )}
    </div>
  );
};

interface LegendItemProps {
  color: string;
  label: string;
  percentage: number;
  isActive: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
}

const LegendItem = ({ 
  color, 
  label, 
  percentage, 
  isActive,
  onMouseEnter,
  onMouseLeave,
  onClick
}: LegendItemProps) => {
  // Detect if we're on mobile
  const [isMobile, setIsMobile] = useState<boolean>(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  return (
    <div 
      className={`flex items-center gap-1.5 rounded-md py-0.5 transition-all duration-300 cursor-pointer hover:bg-gray-50 ${isActive ? 'bg-gray-50 font-medium' : 'opacity-100'}`}
      onMouseEnter={isMobile ? undefined : onMouseEnter}
      onMouseLeave={isMobile ? undefined : onMouseLeave}
      onClick={onClick}
    >
      <div 
        className="w-2 h-2 rounded-sm transition-all flex-shrink-0" 
        style={{ 
          backgroundColor: color,
          transform: isActive ? 'scale(1.2)' : 'scale(1)',
          boxShadow: isActive ? '0 0 3px rgba(0,0,0,0.3)' : 'none'
        }}
      />
      <span className={`text-xs text-gray-700 truncate flex-1 transition-all ${isActive ? 'text-gray-900' : ''}`}>{label}</span>
      <span className={`text-xs font-medium flex-shrink-0 transition-all ${isActive ? 'text-[#1A5173]' : 'text-gray-600'}`}>{percentage}%</span>
    </div>
  );
};

interface MonthlyDonutChartProps {
  totalCost: number;
  monthlyPayment: number;
  propertyTax: number;
  homeInsurance: number;
  mortgageInsurance: number;
  maintenance: number;
  utilities: number;
}

export const MonthlyDonutChart = ({ 
  totalCost, 
  monthlyPayment, 
  propertyTax, 
  homeInsurance,
  mortgageInsurance,
  maintenance,
  utilities
}: MonthlyDonutChartProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  
  // Detect if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Handle hover or click based on device type
  const handleInteraction = (index: number | null) => {
    if (isMobile) {
      // For mobile: toggle the active index
      setActiveIndex(prevIndex => prevIndex === index ? null : index);
    } else {
      // For desktop: set to hovered index
      setActiveIndex(index);
    }
  };
  
  // Format percentage calculation function
  const calculatePercentage = (value: number): number => Math.round((value / totalCost) * 100);
  
  // Improved color palette with better differentiation but still thematically cohesive
  // Using a blue-green primary palette but with distinct hues for each segment
  const chartData: ChartDataItem[] = [
    { 
      label: 'Mortgage', 
      value: monthlyPayment, 
      total: totalCost, 
      color: '#1A5173', // Primary dark blue from the theme
      percentage: calculatePercentage(monthlyPayment)
    },
    { 
      label: 'Property Tax', 
      value: propertyTax, 
      total: totalCost, 
      color: '#604079', // Deep purple for contrast
      percentage: calculatePercentage(propertyTax)
    },
    { 
      label: 'Insurance', 
      value: homeInsurance, 
      total: totalCost, 
      color: '#3A7561', // Muted green - distinct from other greens
      percentage: calculatePercentage(homeInsurance)
    },
    ...(mortgageInsurance > 0 ? [{
      label: 'Mortgage Insurance', 
      value: mortgageInsurance, 
      total: totalCost, 
      color: '#E68A2E', // Orange/amber for warm contrast
      percentage: calculatePercentage(mortgageInsurance)
    }] : []),
    { 
      label: 'Maintenance', 
      value: maintenance, 
      total: totalCost, 
      color: '#00513B', // Dark green from new theme
      percentage: calculatePercentage(maintenance)
    },
    { 
      label: 'Utilities', 
      value: utilities, 
      total: totalCost, 
      color: '#2D6E92', // Lighter blue that's different from mortgage blue
      percentage: calculatePercentage(utilities)
    }
  ].filter(item => item.value > 0); // Only include items with values > 0

  return (
    <div className="flex flex-row gap-1">
      <div className="flex-shrink-0">
        <DonutChart 
          data={chartData} 
          size={160}
          strokeWidth={30}
          activeIndex={activeIndex}
          onHover={handleInteraction}
        />
      </div>
      
      <div className="flex-grow space-y-0.5 min-w-0 max-w-[130px] pt-5">
        {chartData.map((item, index) => (
          <LegendItem
            key={index}
            color={item.color}
            label={item.label}
            percentage={item.percentage}
            isActive={activeIndex === index}
            onMouseEnter={() => !isMobile && handleInteraction(index)}
            onMouseLeave={() => !isMobile && handleInteraction(null)}
            onClick={() => handleInteraction(index)}
          />
        ))}
      </div>
    </div>
  );
};

// Standalone box component for the donut chart
export const CostBreakdownBox = ({
  totalCost, 
  monthlyPayment, 
  propertyTax, 
  homeInsurance,
  mortgageInsurance,
  maintenance,
  utilities
}: MonthlyDonutChartProps) => {
  return (
    <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm h-full">
      <div className="flex items-center gap-2 mb-0">
        <PieChart className="w-5 h-5 text-[#1A5173]" />
        <Tooltip content="Visual breakdown of your monthly housing expenses. Each segment shows a cost category as a percentage of total monthly expenses. Hover or tap segments for details.">
          <h3 className="text-sm font-medium text-gray-700">Estimated Total Monthly Costs Chart</h3>
        </Tooltip>
      </div>
      
      <div className="flex justify-center -mt-2">
        <MonthlyDonutChart
          totalCost={totalCost}
          monthlyPayment={monthlyPayment}
          propertyTax={propertyTax}
          homeInsurance={homeInsurance}
          mortgageInsurance={mortgageInsurance}
          maintenance={maintenance}
          utilities={utilities}
        />
      </div>
    </div>
  );
};

// Example component showing usage with sample data
export default function ImprovedDonutChartExample() {
  // Sample data for demonstration
  const sampleData = {
    totalCost: 3484,
    monthlyPayment: 2187, // ~63%
    propertyTax: 381,     // ~11%
    homeInsurance: 114,   // ~3%
    mortgageInsurance: 170, // ~5%
    maintenance: 381,     // ~11%
    utilities: 250        // ~7%
  };
  
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="p-1">
        <CostBreakdownBox {...sampleData} />
      </div>
    </div>
  );
}